import React, { useRef, useCallback } from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import {
  LockOutlined,
  DashboardOutlined,
  CloudDownloadOutlined,
  PersonAddOutlined,
  HelpOutlineOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import type { SupportCategory } from '../../types';

interface CategoryOption {
  id: SupportCategory;
  label: string;
  icon: React.ReactElement;
}

const CATEGORIES: CategoryOption[] = [
  { id: 'login', label: "Can't Log In", icon: <LockOutlined /> },
  { id: 'study-dashboard', label: 'Dashboard Issue', icon: <DashboardOutlined /> },
  { id: 'data-extraction', label: 'Data Error', icon: <CloudDownloadOutlined /> },
  { id: 'enrollment', label: 'Enrollment', icon: <PersonAddOutlined /> },
  { id: 'other', label: 'Other', icon: <HelpOutlineOutlined /> },
];

interface CategorySelectorProps {
  selected: SupportCategory | null;
  onSelect: (category: SupportCategory) => void;
}

const MotionBox = motion.create(Box);

const CategorySelector: React.FC<CategorySelectorProps> = ({ selected, onSelect }) => {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      let nextIndex = index;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (index + 1) % CATEGORIES.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (index - 1 + CATEGORIES.length) % CATEGORIES.length;
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(CATEGORIES[index].id);
        return;
      } else {
        return;
      }
      itemRefs.current[nextIndex]?.focus();
    },
    [onSelect]
  );

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 8 },
    visible: shouldReduceMotion
      ? { opacity: 1 }
      : { opacity: 1, y: 0, transition: { type: 'spring' as const, damping: 20, stiffness: 300 } },
  };

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 500 }}
      >
        What do you need help with?
      </Typography>
      <MotionBox
        role="radiogroup"
        aria-label="Issue category"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
        }}
      >
        {CATEGORIES.map((cat, index) => {
          const isSelected = selected === cat.id;
          return (
            <motion.div key={cat.id} variants={itemVariants}>
              <ButtonBase
                ref={(el) => { itemRefs.current[index] = el as HTMLButtonElement; }}
                role="radio"
                aria-checked={isSelected}
                tabIndex={isSelected || (!selected && index === 0) ? 0 : -1}
                onClick={() => onSelect(cat.id)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                component={motion.button}
                whileTap={shouldReduceMotion ? undefined : { scale: 0.97 }}
                animate={
                  isSelected && !shouldReduceMotion
                    ? { scale: 1.03 }
                    : { scale: 1 }
                }
                transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  p: 1.5,
                  borderRadius: '8px',
                  border: '1.5px solid',
                  borderColor: isSelected
                    ? theme.palette.primary.main
                    : theme.palette.divider,
                  backgroundColor: isSelected
                    ? `${theme.palette.primary.main}08`
                    : 'transparent',
                  width: '100%',
                  minHeight: 72,
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.light,
                    backgroundColor: `${theme.palette.primary.main}04`,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    color: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                    display: 'flex',
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                  }}
                >
                  {cat.icon}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    textAlign: 'center',
                    lineHeight: 1.2,
                    fontSize: '0.7rem',
                  }}
                >
                  {cat.label}
                </Typography>
              </ButtonBase>
            </motion.div>
          );
        })}
      </MotionBox>
    </Box>
  );
};

export default CategorySelector;
